import {
  arePlayerNotificationsEnabled,
  loadPlayerNotificationPrefs,
  savePlayerNotificationPrefs,
  DEFAULT_PLAYER_NOTIFICATION_PREFS,
  PLAYER_NOTIFICATION_PREFS_KEY,
} from '../utils/playerNotificationPrefs';
// @ts-ignore — JS module
import notificationService from './notificationService.js';

export interface TournamentNotificationTarget {
  tournamentId: string;
  playerId?: string;
}

/**
 * Subscribe (or unsubscribe) push notifications for every roster player row that
 * matches the signed-in account's US Chess ID, when notification prefs allow it.
 */
export async function syncPlayerTournamentNotifications(
  playerName: string,
  uscfId: string | undefined,
  tournaments: TournamentNotificationTarget[],
  options?: { requestPermission?: boolean }
): Promise<void> {
  const normalizedUscf = String(uscfId || '').replace(/\D/g, '').trim();
  if (!normalizedUscf) return;
  if (!(notificationService as any).isSupported) return;

  const prefs = loadPlayerNotificationPrefs();
  const enabled = arePlayerNotificationsEnabled(prefs);
  const rosterTournaments = tournaments.filter((t) => t.playerId);

  if (!enabled) {
    for (const tournament of rosterTournaments) {
      try {
        await (notificationService as any).unsubscribeFromPlayerNotifications(
          tournament.tournamentId,
          tournament.playerId!,
          playerName
        );
      } catch {
        // ignore per-tournament unsubscribe errors
      }
    }
    return;
  }

  if (Notification.permission === 'denied') return;

  if (Notification.permission === 'default' && options?.requestPermission) {
    await (notificationService as any).requestPermission();
  } else if (Notification.permission === 'granted') {
    await (notificationService as any).subscribeToPush();
  }

  if (Notification.permission !== 'granted') return;

  for (const tournament of rosterTournaments) {
    const playerId = tournament.playerId!;
    try {
      const subscribed = await (notificationService as any).isSubscribedToPlayer(
        tournament.tournamentId,
        playerId,
        playerName
      );
      if (!subscribed) {
        await (notificationService as any).subscribeToPlayerNotifications(
          tournament.tournamentId,
          playerId,
          playerName
        );
      }
    } catch (err) {
      console.warn('Failed to sync tournament notifications:', tournament.tournamentId, err);
    }
  }
}

/** Seed default prefs for registered players when none are saved yet. */
export function ensureDefaultPlayerNotificationPrefs(hasRegistrations: boolean): void {
  if (!hasRegistrations) return;
  const saved = localStorage.getItem(PLAYER_NOTIFICATION_PREFS_KEY);
  if (!saved) {
    savePlayerNotificationPrefs(DEFAULT_PLAYER_NOTIFICATION_PREFS);
    return;
  }
  try {
    const parsed = JSON.parse(saved);
    savePlayerNotificationPrefs({ ...DEFAULT_PLAYER_NOTIFICATION_PREFS, ...parsed });
  } catch {
    savePlayerNotificationPrefs(DEFAULT_PLAYER_NOTIFICATION_PREFS);
  }
}
